import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, Segmented, Select, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  needsOverride,
  OperationEventClient,
  ShopFloorClient,
  type ProductionOperationEventDTO,
  type ProductionOrderOperationDTO,
  type StepReportRequest,
} from "../../../api/floor";
import { ScrapReasonClient } from "../../../api/lotReference";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import { ProgressBar } from "../../../components/ProgressBar";
import { useOverridePrompt } from "../OverridePrompt";
import { StepStatusBadge } from "../badges";
import { countsCutboards, stepShare, stepTone, when } from "./lotModel";
import { eventKindLabel } from "./HistoryTab";

type Mode = "report" | "send" | "missing" | "take-back";

/**
 * One step of a lot, from the office: what it needs and has done, the
 * machines and program, every event reported on it, and the same reports the
 * kiosk sends (good and scrapped parts, missing, finish, skip, reopen).
 *
 * <p>A report past what the steps it waits for have passed comes back as a 409
 * OVERRIDE; the dialog asks why and resends with the reason, which the event
 * keeps with who and by how much. An external step (Paint) is reported as
 * sent to the painter, then back.</p>
 */
export function StepDialog({
  step,
  editable,
  onClose,
  onChanged,
}: {
  step: ProductionOrderOperationDTO;
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const override = useOverridePrompt(false);
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  const [mode, setMode] = useState<Mode>("report");
  const [good, setGood] = useState("");
  const [scrap, setScrap] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [missing, setMissing] = useState(String(Number(step.quantity_missing ?? 0)));
  const [note, setNote] = useState("");

  const events = useQuery({
    queryKey: ["production", "operation-event", "step", step.id],
    queryFn: async () =>
      (
        await new OperationEventClient(http).find({
          page: 0,
          size: 200,
          filters: { production_order_operation_id: [String(step.id)] },
          sort: ["recorded_at,desc"],
        })
      ).content,
  });
  const reasons = useQuery({
    queryKey: ["production", "scrap-reason", "active"],
    queryFn: () => new ScrapReasonClient(http).active(),
    staleTime: 10 * 60_000,
    enabled: editable,
  });

  const send = useMutation({
    mutationFn: async (request: StepReportRequest) => {
      try {
        return await floor.report(step.id, request);
      } catch (failure) {
        const needed = needsOverride(failure);
        if (needed === null) throw failure;
        const answer = await override.ask(needed);
        if (!answer) return null;
        return floor.report(step.id, { ...request, override_reason: answer.reason });
      }
    },
    onSuccess: async (updated) => {
      if (!updated) return;
      setGood("");
      setSent("");
      setScrap("");
      setNote("");
      await onChanged();
      await events.refetch();
      toast.success(t("lot.step.reported", "Reported"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const external = Boolean(step.is_external);
  const [sent, setSent] = useState("");
  const sentValue = sent.trim() === "" ? 0 : Number(sent);
  const goodValue = good.trim() === "" ? 0 : Number(good);
  // Taking parts back (a report made by mistake): a quantity, never more than reported, and why.
  const [takeBack, setTakeBack] = useState("");
  const [takeBackWhy, setTakeBackWhy] = useState("");
  const takeBackValue = takeBack.trim() === "" ? 0 : Number(takeBack);
  const takeBackValid = Number.isFinite(takeBackValue) && takeBackValue > 0
    && takeBackValue <= Number(step.quantity_done ?? 0) && takeBackWhy.trim() !== "";
  const scrapValue = scrap.trim() === "" ? 0 : Number(scrap);
  const reportValid =
    Number.isFinite(goodValue) && goodValue >= 0 && Number.isFinite(scrapValue) && scrapValue >= 0 && (goodValue > 0 || scrapValue > 0) && (scrapValue === 0 || reasonId !== "");
  const missingValue = Number(missing);
  const finished = step.status === "DONE" || step.status === "SKIPPED";
  const cutboards = countsCutboards(step);
  const unit = cutboards ? t("lot.unit.cutboards", "cutboards") : t("lot.unit.parts", "parts");
  const text = (value: string) => (value.trim() === "" ? null : value.trim());

  const act = async (kind: "FINISH" | "SKIP" | "REOPEN" | "START") => {
    if (kind === "SKIP") {
      const yes = await confirm({
        title: t("lot.step.skip.title", "Skip this step?"),
        message: t("lot.step.skip.message", "It is not needed on this lot. It stops counting in the lot's progress."),
        confirmLabel: t("lot.step.skip", "Skip"),
      });
      if (!yes) return;
    }
    if (kind === "FINISH" && stepShare(step) < 1) {
      const yes = await confirm({
        title: t("lot.step.finish.title", "Finish this step short?"),
        message: t("lot.step.finish.message", "Fewer parts were reported than it needs. Finishing it counts it as done anyway."),
        confirmLabel: t("lot.step.finish", "Finish"),
      });
      if (!yes) return;
    }
    send.mutate({ kind, note: text(note) });
  };

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={`${step.component_code ?? t("lot.lot-level", "Whole lot")} · ${step.process_name ?? step.process_code ?? ""}`}
      description={[step.letter, step.part_number, step.part_name].filter(Boolean).join(" · ")}
      actions={
        <Button variant="ghost" onClick={onClose}>
          {t("dialog.close", "Close")}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <Figure
            label={t("lot.step.status", "Status")}
            value={
              <span className="flex flex-wrap items-center gap-1.5">
                <StepStatusBadge status={step.status} />
                {step.has_override && (
                  <Badge variant="warning" size="sm">
                    {t("lot.step.overridden", "override")}
                  </Badge>
                )}
              </span>
            }
          />
          {external ? (
            <Figure
              label={t("lot.step.sent-back", "Sent · back")}
              value={`${formatNumber(step.quantity_sent ?? 0)} · ${formatNumber(step.quantity_done ?? 0)} / ${formatNumber(step.quantity_required)} ${unit}`}
            />
          ) : (
            <Figure
              label={t("lot.step.done", "Done")}
              value={`${formatNumber(step.quantity_done ?? 0)} / ${formatNumber(step.quantity_required_counted ?? step.quantity_required)} ${unit}`}
            />
          )}
          <Figure label={t("lot.step.scrap", "Scrap")} value={formatNumber(step.quantity_scrap ?? 0)} />
          <Figure
            label={t("lot.step.missing", "Missing")}
            value={
              Number(step.quantity_missing ?? 0) > 0 ? (
                <Badge variant="error" size="sm">
                  {formatNumber(step.quantity_missing)}
                </Badge>
              ) : (
                "0"
              )
            }
          />
        </div>
        <ProgressBar value={stepShare(step)} tone={stepTone(step)} />
        <div className="grid gap-2 text-[13px] sm:grid-cols-2">
          <p>
            <span className="text-ink-muted">{t("lot.step.may-do", "May do in total")}: </span>
            {formatNumber(step.quantity_available ?? 0)} {t("lot.unit.parts", "parts")}
            <span className="text-ink-faint"> · {t("lot.step.may-do-hint", "what the steps it waits for have passed")}</span>
          </p>
          <p>
            <span className="text-ink-muted">{t("lot.step.machines", "Machines")}: </span>
            {step.station_codes || t("lot.step.any-machine", "any machine of the process")}
          </p>
          {step.program_code && (
            <p>
              <span className="text-ink-muted">{t("lot.step.program", "Program")}: </span>
              <span className="font-mono">{step.program_code}</span>
            </p>
          )}
          {cutboards && (
            <p className="text-ink-muted">
              {t("lot.step.cutboard-hint", "Nested part: this step counts cutboards, {{n}} parts each.", {
                n: Math.round(Number(step.quantity_required) / Math.max(1, Number(step.quantity_required_counted ?? 1))),
              })}
            </p>
          )}
          {step.is_derived && <p className="text-ink-muted">{t("lot.step.derived", "Step taken from the part's fields: its revision has no routing.")}</p>}
          {step.note && <p className="sm:col-span-2">{step.note}</p>}
        </div>

        {editable && (
          <div className="flex flex-col gap-3 rounded-md border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                value={mode}
                onChange={(value) => setMode(value as Mode)}
                options={[
                  ...(external ? [{ value: "send", label: t("lot.step.mode.send", "Sent to painter") }] : []),
                  { value: "report", label: external ? t("lot.step.mode.back", "Back from painter") : t("lot.step.mode.report", "Quantities") },
                  { value: "missing", label: t("lot.step.mode.missing", "Missing") },
                  ...(Number(step.quantity_done ?? 0) > 0 ? [{ value: "take-back", label: t("lot.step.mode.take-back", "Take back") }] : []),
                ]}
              />
              <div className="ml-auto flex flex-wrap gap-2">
                {step.status === "PENDING" && (
                  <Button size="sm" variant="ghost" disabled={send.isPending} onClick={() => void act("START")}>
                    {t("lot.step.start", "Start")}
                  </Button>
                )}
                {!finished && (
                  <>
                    <Button size="sm" variant="ghost" disabled={send.isPending} onClick={() => void act("SKIP")}>
                      {t("lot.step.skip", "Skip")}
                    </Button>
                    <Button size="sm" variant="secondary" disabled={send.isPending} onClick={() => void act("FINISH")}>
                      {t("lot.step.finish", "Finish")}
                    </Button>
                  </>
                )}
                {finished && (
                  <Button size="sm" variant="secondary" disabled={send.isPending} onClick={() => void act("REOPEN")}>
                    {t("lot.step.reopen", "Reopen")}
                  </Button>
                )}
              </div>
            </div>
            {mode === "send" ? (
              <div className="grid gap-3 sm:grid-cols-4">
                <Field label={`${t("lot.step.sent", "Sent")} (${unit})`} htmlFor="step-sent">
                  <Input id="step-sent" type="number" min="0" step="any" value={sent} invalid={sentValue < 0} onChange={(event) => setSent(event.target.value)} />
                </Field>
                <Field label={t("column.note", "Note")} htmlFor="step-sent-note" className="sm:col-span-3">
                  <Input id="step-sent-note" value={note} onChange={(event) => setNote(event.target.value)} />
                </Field>
                <div className="sm:col-span-4">
                  <Button
                    variant="primary"
                    disabled={!Number.isFinite(sentValue) || sentValue <= 0}
                    loading={send.isPending}
                    onClick={() => send.mutate({ kind: "SEND", quantity_sent: sentValue, note: text(note) })}
                  >
                    {t("lot.step.record-sent", "Record sent")}
                  </Button>
                </div>
              </div>
            ) : mode === "report" ? (
              <div className="grid gap-3 sm:grid-cols-4">
                <Field
                  label={`${t("lot.step.good", "Good")} (${unit})`}
                  htmlFor="step-good"
                  hint={t("lot.step.good-hint.v2", "A report made by mistake is taken back with Take back.")}
                >
                  <Input id="step-good" type="number" min="0" step="any" value={good} invalid={goodValue < 0} onChange={(event) => setGood(event.target.value)} />
                </Field>
                <Field label={t("lot.step.scrap", "Scrap")} htmlFor="step-scrap">
                  <Input id="step-scrap" type="number" min="0" step="any" value={scrap} onChange={(event) => setScrap(event.target.value)} />
                </Field>
                <Field label={t("lot.step.reason", "Scrap reason")} htmlFor="step-reason" required={scrapValue > 0}>
                  <Select
                    id="step-reason"
                    value={reasonId}
                    placeholder={t("form.choose", "Choose…")}
                    disabled={scrapValue <= 0}
                    options={(reasons.data ?? []).map((reason) => ({
                      value: String(reason.id),
                      label: i18n.language.startsWith("fr") ? reason.name_fr : reason.name,
                    }))}
                    onChange={(event) => setReasonId(event.target.value)}
                  />
                </Field>
                <Field label={t("column.note", "Note")} htmlFor="step-note">
                  <Input id="step-note" value={note} onChange={(event) => setNote(event.target.value)} />
                </Field>
                <div className="sm:col-span-4">
                  <Button
                    variant="primary"
                    disabled={!reportValid}
                    loading={send.isPending}
                    onClick={() =>
                      send.mutate({
                        kind: "REPORT",
                        quantity_good: goodValue,
                        quantity_scrap: scrapValue,
                        production_scrap_reason_id: scrapValue > 0 ? Number(reasonId) : null,
                        note: text(note),
                      })
                    }
                  >
                    {t("lot.step.report", "Report")}
                  </Button>
                </div>
              </div>
            ) : mode === "take-back" ? (
              <div className="grid gap-3 sm:grid-cols-4">
                <Field
                  label={`${t("lot.step.take-back", "To take back")} (${unit})`}
                  htmlFor="step-take-back"
                  hint={t("lot.step.take-back.hint", "At most the {{n}} reported.", { n: formatNumber(step.quantity_done ?? 0) })}
                >
                  <Input id="step-take-back" type="number" min="0" max={String(Number(step.quantity_done ?? 0))} step="any" value={takeBack}
                    invalid={takeBackValue < 0 || takeBackValue > Number(step.quantity_done ?? 0)} onChange={(event) => setTakeBack(event.target.value)} />
                </Field>
                <Field label={t("lot.step.take-back.why", "Why")} htmlFor="step-take-back-why" required className="sm:col-span-3">
                  <Input id="step-take-back-why" value={takeBackWhy} onChange={(event) => setTakeBackWhy(event.target.value)} />
                </Field>
                <div className="sm:col-span-4">
                  <Button
                    variant="primary"
                    disabled={!takeBackValid}
                    loading={send.isPending}
                    onClick={() => send.mutate({ kind: "REPORT", quantity_good: -takeBackValue, note: takeBackWhy.trim() })}
                  >
                    {t("lot.step.take-back.confirm", "Take back")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label={t("lot.step.missing-now", "Missing now")} htmlFor="step-missing" hint={t("lot.step.missing-hint", "0 clears it.")}>
                  <Input id="step-missing" type="number" min="0" step="any" value={missing} onChange={(event) => setMissing(event.target.value)} />
                </Field>
                <Field label={t("column.note", "Note")} htmlFor="step-missing-note" className="sm:col-span-2">
                  <Input id="step-missing-note" value={note} onChange={(event) => setNote(event.target.value)} />
                </Field>
                <div className="sm:col-span-3">
                  <Button
                    variant="primary"
                    disabled={!(missingValue >= 0)}
                    loading={send.isPending}
                    onClick={() => send.mutate({ kind: "MISSING", quantity_missing: missingValue, note: text(note) })}
                  >
                    {t("lot.step.report-missing", "Record missing")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <h4 className="text-[12px] font-medium uppercase tracking-wide text-ink-muted">{t("lot.step.events", "Reported")}</h4>
          {(events.data ?? []).length === 0 ? (
            <p className="text-[13px] text-ink-muted">
              {events.isLoading ? t("common.loading", "Loading…") : t("lot.step.no-events", "Nothing reported on this step yet.")}
            </p>
          ) : (
            <ul className="max-h-64 overflow-auto rounded-md border border-line text-[13px]">
              {(events.data ?? []).map((event) => (
                <EventLine key={event.id} event={event} language={i18n.language} />
              ))}
            </ul>
          )}
        </div>
      </div>
      {override.element}
    </Dialog>
  );
}

function Figure({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">{label}</p>
      <p className="mt-1 text-[14px] tabular-nums">{value}</p>
    </div>
  );
}

function EventLine({ event, language }: { event: ProductionOperationEventDTO; language: string }) {
  const { t } = useTranslation();
  const figures = [
    Number(event.quantity_sent ?? 0) !== 0 ? t("lot.event.sent", "{{n}} sent", { n: formatNumber(event.quantity_sent) }) : null,
    Number(event.quantity_good) !== 0 ? `${Number(event.quantity_good) > 0 ? "+" : ""}${formatNumber(event.quantity_good)}` : null,
    Number(event.quantity_scrap) > 0 ? t("lot.event.scrap", "{{n}} scrap", { n: formatNumber(event.quantity_scrap) }) : null,
    event.kind === "MISSING" ? t("lot.event.missing", "{{n}} missing", { n: formatNumber(event.quantity_missing ?? 0) }) : null,
  ].filter(Boolean);
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-line px-3 py-1.5 last:border-b-0">
      <span className="w-36 shrink-0 text-[12px] text-ink-muted">{when(event.recorded_at, language)}</span>
      <span className="font-medium">{eventKindLabel(t, event.kind)}</span>
      {figures.length > 0 && <span className="tabular-nums">{figures.join(" · ")}</span>}
      {event.scrap_reason_name && <span className="text-ink-muted">{event.scrap_reason_name}</span>}
      {event.is_override && <OverrideMark event={event} />}
      <span className="ml-auto text-[12px] text-ink-faint">{[event.station_code, event.operator_name].filter(Boolean).join(" · ")}</span>
      {event.note && <span className="w-full text-[12px] text-ink-muted">{event.note}</span>}
    </li>
  );
}

/** "Override +1: part from lot 900123", on an event reported past what the step allowed. */
export function OverrideMark({ event }: { event: ProductionOperationEventDTO }) {
  const { t } = useTranslation();
  return (
    <span title={event.override_reason ?? undefined}>
      <Badge variant="warning" size="sm">
        {t("lot.event.override", "Override")}
        {event.quantity_over != null ? ` +${formatNumber(event.quantity_over)}` : ""}
        {event.override_reason ? `: ${event.override_reason}` : ""}
      </Badge>
    </span>
  );
}
